import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Avatar } from 'primereact/avatar';
import { PanelMenu } from 'primereact/panelmenu';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import {
  AddIcon,
  EditIcon,
  CopyIcon,
  DeleteIcon,
  PrintIcon,
  SearchIcon,
  ExportIcon,
  RefreshIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
} from '../../svg/svgIcons';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { DateTime } from 'luxon';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { TABULATOR_LIST_ID_MIN_WIDTH, tabulatorDateUserColumnMinWidth } from '../../../utils/tabulatorColumnMinWidth';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse, sanitizeTabulatorLayoutAttributes } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';

const PAGE_ID = 'ChecklistHolidayList';

const CHECKLIST_HOLIDAY_MASK_FIELD_I18N = Object.freeze({
  id: 'ChecklistHolidayList.Column.Id',
  name: 'ChecklistHolidayList.Column.Name',
  date_from: 'ChecklistHolidayList.Column.Period',
  scope_type: 'ChecklistHolidayList.Column.Scope',
  user_id: 'ChecklistHolidayList.Column.DepartmentsOrUser',
  substitute_user_id: 'ChecklistHolidayList.Column.Substitute',
  created_at: 'ChecklistHolidayList.Column.CreatedAt',
  updated_at: 'ChecklistHolidayList.Column.UpdatedAt',
});

const CHECKLIST_HOLIDAY_COLUMN_I18N_KEYS = CHECKLIST_HOLIDAY_MASK_FIELD_I18N;

const DEFAULT_COLUMNS_VISIBLE_HOLIDAY = Object.freeze({
  id: true,
  name: true,
  date_from: true,
  scope_type: true,
  user_id: true,
  substitute_user_id: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_HOLIDAY };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_HOLIDAY };
  return merged;
}

function normalizeHolidayColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function removeDiacritics(str) {
  return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function diacriticInsensitiveFilter(headerValue, rowValue) {
  if (!headerValue) return true;
  if (!rowValue) return false;
  const normalize = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return normalize(String(rowValue)).includes(normalize(String(headerValue)));
}

export default function ChecklistHolidayList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    toast,
    setUserMaskFilter,
    setStandardMaskFilter,
    openFavoriteDialog,
    setFastView,
    setDialogText,
    setVisible,
    openChosenNotificationPreferences,
    getDateFormat,
    formatDate,
    getUserInitials,
    getUserColor,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_HOLIDAY });
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('portrait');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const dateLocale = useMemo(() => (locale === 'de' ? 'de-DE' : (locale === 'en' || locale === 'gb') ? 'en-GB' : 'sk-SK'), [locale]);

  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!row) return false;
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const refreshList = useCallback((showDialog = false) => {
    const table = getTable();
    if (showDialog) {
      table?.alert?.(t('ChecklistHolidayList.Loading.Updating'));
    }
    getRequest('/api/checklist-holidays')
      .then((response) => {
        const data = Array.isArray(response) ? response : response?.data ?? [];
        dispatch({ type: 'load_checklist_holidays', newChecklistHolidays: data });
      })
      .catch(() => {})
      .finally(() => {
        if (showDialog) table?.clearAlert?.();
      });
  }, [dispatch, getTable, t]);

  const rejectDelete = useCallback(() => {
    toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.RejectedSummary'), detail: t('ServiceTask.RequestCancelled'), life: 3000 });
  }, [t, toast]);

  const acceptDelete = useCallback(() => {
    const rows_to_delete = getTable()?.getSelectedData?.() || [];
    setDialogText?.(t('ChecklistHolidayList.Loading.Deleting'));
    setVisible?.(true);
    const deletePromises = rows_to_delete.map((element) =>
      deleteRequest(`/api/checklist-holidays/${element.id}`)
    );
    Promise.allSettled(deletePromises).then((results) => {
      setVisible?.(false);
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.filter((r) => r.status === 'rejected').length;
      if (successCount > 0) refreshList();
      if (successCount > 0 && failCount === 0) {
        toast?.current?.show({
          severity: 'info',
          summary: t('ChecklistHolidayList.Toast.DeletedSummary'),
          life: 3000,
          detail: successCount === 1 ? t('ChecklistHolidayList.Toast.DeletedOneDetail') : t('ChecklistHolidayList.Toast.DeletedManyDetail').replace('{{count}}', String(successCount)),
        });
      } else if (successCount > 0 && failCount > 0) {
        toast?.current?.show({
          severity: 'warn',
          summary: t('ChecklistHolidayList.Toast.PartialDeletedSummary'),
          detail: t('ChecklistHolidayList.Toast.PartialDeletedDetail').replace('{{success}}', String(successCount)).replace('{{fail}}', String(failCount)),
          life: 3000,
        });
      } else if (failCount > 0) {
        toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.RejectedSummary'), detail: t('ChecklistHolidayList.Toast.DeleteFailedDetail'), life: 3000 });
      }
    });
  }, [getTable, refreshList, setDialogText, setVisible, t, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ChecklistHolidayList.ConfirmDeleteSelected'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: acceptDelete,
      reject: rejectDelete,
    });
  }, [acceptDelete, rejectDelete, t]);

  const onSelectRow = (data) => {
    if (!data || data.length === 0) {
      setEditIcon(true);
      setCopyIcon(true);
      setDeleteIcon(true);
    } else {
      if (data.length === 1) {
        if (user?.permissions?.includes('add settings')) setCopyIcon(false);
        if (user?.permissions?.includes('edit settings')) setEditIcon(false);
      }
      if (user?.permissions?.includes('delete settings')) setDeleteIcon(false);
    }
  };

  function onEditRow(rowData) {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistHolidayList.Tab.Edit'), translationKey: 'ChecklistHolidayList.Tab.Edit', legacyLabel: t('ChecklistHolidayList.Tab.Edit'),
      page: 'ChecklistHolidayAdd',
      props: { holiday: rowData, type: 'edit', not_last_used: true },
      mask: 'chck014',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function periodFormatter(cell) {
    const row = cell.getRow().getData();
    const from = row.date_from ? new Date(row.date_from).toLocaleDateString(dateLocale) : '';
    const to = row.date_to ? new Date(row.date_to).toLocaleDateString(dateLocale) : '';
    const sep = t('ChecklistHolidayList.PeriodRangeSeparator');
    return `${from}${sep}${to}`;
  }

  function scopeFormatter(cell) {
    const v = cell.getValue();
    if (v === 'DEPARTMENTS') return t('ChecklistHolidayList.Scope.Departments');
    if (v === 'USER') return t('ChecklistHolidayList.Scope.User');
    return v;
  }

  function departmentsOrUserFormatter(cell) {
    const row = cell.getRow().getData();
    if (row.scope_type === 'DEPARTMENTS') {
      const ids = row.department_ids || [];
      if (ids.length === 0 && row.selection_mode === 'INCLUDE') return t('ChecklistHolidayList.AllDepartments');
      const mode = row.selection_mode === 'INCLUDE' ? t('ChecklistHolidayList.Selection.Include') : row.selection_mode === 'ALL_EXCEPT' ? t('ChecklistHolidayList.Selection.AllExcept') : '';
      const depts = (state.departments || []).filter((d) => ids.includes(d.id));
      const names = depts.map((d) => d.name).join(', ') || ids.join(', ');
      const dash = t('ChecklistHolidayList.Dash');
      return mode ? `${mode}: ${names || dash}` : names || dash;
    }
    if (row.user) {
      return row.user?.employee?.name || row.user?.username || `#${row.user_id}`;
    }
    return `#${row.user_id}`;
  }

  function substituteFormatter(cell) {
    const row = cell.getRow().getData();
    if (row.scope_type !== 'USER' || !row.substitute_user_id) return t('ChecklistHolidayList.Dash');
    const sub = row.substitute_user;
    return sub?.employee?.name || sub?.username || `#${row.substitute_user_id}`;
  }

  function periodHeaderFilter(headerValue, rowValue, rowData) {
    if (!headerValue) return true;
    const from = rowData.date_from ? new Date(rowData.date_from).toLocaleDateString(dateLocale) : '';
    const to = rowData.date_to ? new Date(rowData.date_to).toLocaleDateString(dateLocale) : '';
    const text = `${from} ${to}`;
    return removeDiacritics(text).toLowerCase().includes(removeDiacritics(headerValue).toLowerCase());
  }

  function departmentsOrUserHeaderFilter(headerValue, rowValue, rowData) {
    if (!headerValue) return true;
    const norm = removeDiacritics(headerValue).toLowerCase();
    if (rowData.scope_type === 'DEPARTMENTS') {
      const ids = rowData.department_ids || [];
      const depts = (state.departments || []).filter((d) => ids.includes(d.id));
      const names = depts.map((d) => d.name).join(' ');
      return removeDiacritics(names).toLowerCase().includes(norm);
    }
    const name = rowData.user?.employee?.name || rowData.user?.username || '';
    return removeDiacritics(name).toLowerCase().includes(norm);
  }

  function substituteHeaderFilter(headerValue, rowValue, rowData) {
    if (!headerValue) return true;
    if (rowData.scope_type !== 'USER' || !rowData.substitute_user) return false;
    const name = rowData.substitute_user?.employee?.name || rowData.substitute_user?.username || '';
    return removeDiacritics(name).toLowerCase().includes(removeDiacritics(headerValue).toLowerCase());
  }

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const userObj = filterParams?.name === 'created_at' ? rowData?.created_by_user : rowData?.last_touched_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  const idHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (!headerValue) return true;
    return String(rowData?.id ?? '').includes(String(headerValue).trim());
  }, []);

  function CreatedAtHolidayFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const raw = rowData?.created_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw ?? '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtHolidayFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.last_touched_by_user;
    const raw = rowData?.updated_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={u?.employee?.name || u?.username}
            label={getUserInitials(u?.employee?.name || u?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    const createdTitle = colTitle('ChecklistHolidayList.Column.CreatedAt');
    const updatedTitle = colTitle('ChecklistHolidayList.Column.UpdatedAt');
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: colTitle('ChecklistHolidayList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.6, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: idHeaderFilter },
      { title: colTitle('ChecklistHolidayList.Column.Name'), field: 'name', hozAlign: 'left', minWidth: 180, widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: diacriticInsensitiveFilter },
      { title: colTitle('ChecklistHolidayList.Column.Period'), field: 'date_from', formatter: periodFormatter, sortable: true, minWidth: 180, widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: periodHeaderFilter },
      { title: colTitle('ChecklistHolidayList.Column.Scope'), field: 'scope_type', formatter: scopeFormatter, minWidth: 140, widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: diacriticInsensitiveFilter },
      { title: colTitle('ChecklistHolidayList.Column.DepartmentsOrUser'), field: 'user_id', formatter: departmentsOrUserFormatter, minWidth: 240, widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: departmentsOrUserHeaderFilter },
      { title: colTitle('ChecklistHolidayList.Column.Substitute'), field: 'substitute_user_id', formatter: substituteFormatter, minWidth: 160, widthGrow: 1, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: substituteHeaderFilter },
      { title: createdTitle, field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(createdTitle), widthGrow: 1, formatter: reactFormatter(<CreatedAtHolidayFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
      { title: updatedTitle, field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(updatedTitle), widthGrow: 1, formatter: reactFormatter(<UpdatedAtHolidayFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
    ];
  }, [dateFmt, dateLocale, dateOrUserFilter, dateTimeFmt, formatDate, getUserColor, getUserInitials, idHeaderFilter, state.departments, t]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: CHECKLIST_HOLIDAY_COLUMN_I18N_KEYS,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const getMaskColumnLabel = useCallback((field) => {
    const key = CHECKLIST_HOLIDAY_MASK_FIELD_I18N[field];
    return key ? t(key) : field;
  }, [t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildHolidayExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runHolidayPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildHolidayExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/checklist-holidays/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('ChecklistHolidayList.Toast.PrintTitle'), detail: e?.message || t('ChecklistHolidayList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildHolidayExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportHolidayXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildHolidayExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.CHECKLIST_HOLIDAYS));
      const { blob, filename } = await downloadBlobGet(`/api/checklist-holidays/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('ChecklistHolidayList.Toast.ExportTitle'), detail: e?.message || t('ChecklistHolidayList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildHolidayExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    setColumnsVisible(mergeVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeHolidayColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.MaskWarnTitle'), detail: t('ChecklistHolidayList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(PAGE_ID, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('ChecklistHolidayList.SaveStandardMaskMessage'),
      header: t('ChecklistHolidayList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        setStandardMaskFilter(PAGE_ID, columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = useCallback(() => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.StandardMaskWarnTitle'), detail: t('ChecklistHolidayList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(standard.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeHolidayColumnVisibilityForSave(mergedCols);
    confirmDialog({
      message: t('ChecklistHolidayList.ApplyStandardMaskMessage'),
      header: t('ChecklistHolidayList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(PAGE_ID, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  }, [closeMenu, getTable, setUserMaskFilter, state.standard_mask_filters, t, toast]);

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_HOLIDAY, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_HOLIDAY, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('ChecklistHolidayList.Toast.MaskWarnTitle'), detail: t('ChecklistHolidayList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_HOLIDAY };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function addNew() {
    dispatch({
      type: 'create_new_tab',
      name: t('ChecklistHolidayList.Tab.New'), translationKey: 'ChecklistHolidayList.Tab.New', legacyLabel: t('ChecklistHolidayList.Tab.New'),
      page: 'ChecklistHolidayAdd',
      props: { holiday: null, type: 'add' },
      mask: 'chck014',
    });
  }

  function copyRow() {
    const selected = getTable()?.getSelectedData?.();
    if (selected && selected[0]) {
      dispatch({
        type: 'create_new_tab',
        name: t('ChecklistHolidayList.Tab.Copy'), translationKey: 'ChecklistHolidayList.Tab.Copy', legacyLabel: t('ChecklistHolidayList.Tab.Copy'),
        page: 'ChecklistHolidayAdd',
        props: { holiday: { ...selected[0], id: undefined }, type: 'add' },
        mask: 'chck014',
      });
    }
  }

  function editSelected() {
    const selected = getTable()?.getSelectedData?.();
    if (selected && selected[0]) onEditRow(selected[0]);
  }

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      {
        key: '0_1',
        label: t('Common.SetFilter'),
        icon: 'pi pi-filter',
        command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); },
        template: itemRenderer,
      },
      {
        key: '0_2',
        label: t('Common.ClearFilter'),
        icon: 'pi pi-filter-slash',
        command: () => {
          setUserMaskFilter(PAGE_ID, 'set_filters', null, [], null);
          getTable()?.clearHeaderFilter?.();
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_3',
        label: t('Common.SetSorting'),
        icon: 'pi pi-sort-alt',
        command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); },
        template: itemRenderer,
      },
      {
        key: '0_4',
        label: t('Common.ClearSorting'),
        icon: 'pi pi-sort-alt-slash',
        command: () => {
          setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, []);
          getTable()?.setSort?.([]);
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_5',
        label: t('Common.SetMask'),
        icon: 'pi pi-server',
        command: openMaskDialog,
        template: itemRenderer,
      },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      {
        key: '0_6',
        label: t('ServiceTask.Notifications'),
        icon: 'pi pi-envelope',
        command: () => { openChosenNotificationPreferences(PAGE_ID); closeMenu(); },
        template: itemRenderer,
      },
    ],
  }], [closeMenu, confirmApplyStandardLayoutToUserMask, getTable, hasApplicableStandardMask, openChosenNotificationPreferences, openMaskDialog, setUserMaskFilter, t]);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') table.clearFilter(true);
    else {
      const needle = normalizeText(normalized);
      table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const handleClickOutside = (e) => {
    if (overlayRef.current && !overlayRef.current.contains(e.target)) setExpandedKeys({});
  };
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const list = state.checklist_holidays || [];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add settings')} onClick={addNew}>
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editSelected}>
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copyRow}>
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
            <button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}>
              <DeleteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => setPrintChoiceVisible(true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; getTable()?.clearFilter?.(true); }}>
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={exportHolidayXlsx} title={t('Common.ExportToExcel')}><ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => refreshList(true)} title={t('Common.Refresh')} aria-label={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tofirst">
            <button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="toup">
            <button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="todown">
            <button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tolast">
            <button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog(t('ChecklistHolidayList.FavoriteTitle'), PAGE_ID)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView(PAGE_ID, columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          {canSetStandardMask ? (
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key="checklist-holiday-list"
                data={list}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask(PAGE_ID, state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      const m = effective.standardMask;
                      isRestoringRef.current = true;
                      const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                      const jsonColumns = safeParse(m.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_HOLIDAY);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents(PAGE_ID, ref, setUserMaskFilter),
                }}
                options={{
                  layout: 'fitColumns',
                  renderHorizontal: 'virtual',
                  movableColumns: true,
                  autoResize: true,
                  headerSortTristate: true,
                  filterMode: 'local',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runHolidayPrint}
      />

      <Dialog header={t('ChecklistHolidayList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('ChecklistHolidayList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400"
            />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center">
              <SearchIcon />
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog header={t('ChecklistHolidayList.MaskDialogTitle')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2 m-1">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('ChecklistHolidayList.MaskSelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('ChecklistHolidayList.MaskDeselectAll')}</button>
          </div>
          {Object.keys(DEFAULT_COLUMNS_VISIBLE_HOLIDAY).map((field) => (
            <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
              <Checkbox inputId={`holiday-mask-${field}`} onChange={(e) => setMask(field, e.checked)} checked={columnsVisible[field] !== false} />
              <label htmlFor={`holiday-mask-${field}`} className="ml-2 cursor-pointer">{getMaskColumnLabel(field)}</label>
            </div>
          ))}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">{t('ChecklistHolidayList.MaskSave')}</button>
        </div>
      </Dialog>
    </>
  );
}
